import { useContext, useEffect } from 'react'
import { TableContext } from 'Context/Table'
import ReactSwitch from 'react-switch'
import { ClientSideTable } from 'Presentation/Components/Table'

import formatNumberCommas from 'Utils/formatNumberCommas'
import { formatNumberAbbreviation } from 'Utils/formatNumberAbbreviation'
import clsx from 'clsx'
import formatNumberCommasNoDecimal from 'Utils/formatNumberCommasNoDecimal'

// Components of Components

const ProductStatus = ({
	value,
	onChange,
}: {
	value: boolean
	onChange?: (checked: boolean) => {}
}) => {
	return (
		<ReactSwitch
			checked={value}
			onChange={() => {}}
			onColor="#0096D4"
			onHandleColor="#ffffff"
			uncheckedIcon={false}
			checkedIcon={false}
			height={20}
			width={36}
			boxShadow="0px 1px 5px rgba(0, 0, 0, 0.6)"
			activeBoxShadow="0px 0px 1px 10px rgba(0, 0, 0, 0.2)"
			className="react-switch"
		/>
	)
}

const ProductVisiblity = ({
	viewAmount,
	favoriteAmount,
	isSub,
}: {
	favoriteAmount: number
	viewAmount: number
	isSub?: boolean
}) => {
	if (isSub) {
		return null
	} else {
		return (
			<div className="d-flex flex-row align-items-center" style={{ columnGap: '12px' }}>
				{/* Favorite */}
				<div className="d-flex flex-row align-items-center">
					<img
						src="/media/icons/kumopack/codicon_heart.svg"
						className="me-1"
						alt="Grey Heart Icon"
					/>
					<div className="text-kumopack-grey-400 fs-7">
						{formatNumberAbbreviation(favoriteAmount)}
					</div>
				</div>

				{/* Views */}
				<div className="d-flex flex-row align-items-center">
					<img src="/media/icons/kumopack/carbon_view.svg" className="me-1" alt="Grey Heart Icon" />
					<div className="text-kumopack-grey-400 fs-7">{formatNumberAbbreviation(viewAmount)}</div>
				</div>
			</div>
		)
	}
}

const ProductType = ({
	value,
	product_num,
	product_img,
	isSub,
}: {
	value: string
	product_num?: string
	product_img: string
	isSub: boolean
}) => {
	return (
		<div
			className={clsx('d-flex align-items-center justify-content-left', {
				'ms-10': isSub,
			})}
			style={{ columnGap: '8px' }}>
			<img
				alt="Logo"
				src={product_img}
				width={!isSub ? 40 : 30}
				height={!isSub ? 40 : 30}
				style={{ borderRadius: '50%' }}
			/>
			<div
				className={clsx('d-flex flex-column', {
					'fs-7': isSub,
				})}>
				<span className="text-kumopack-grey-900">{value}</span>
				{product_num && <span className="text-kumopack-grey-600">SKU: {product_num}</span>}
			</div>
		</div>
	)
}

// eslint-disable-next-line
const ProductChip = ({
	items,
}: {
	items: {
		label: string
		unit: string
	}[]
}) => {
	return (
		<div className="d-flex flex-row align-items-center" style={{ columnGap: '12px' }}>
			{items.map((i, idx) => (
				<span
					className={clsx('badge ', {
						'badge-kumopack-secondary-50 text-kumopack-secondary-700': (idx + 1) % 3 === 0,
						'badge-kumopack-grey-100 text-kumopack-grey-700': (idx + 1) % 2 === 0,
						'badge-kumopack-error-50 text-kumopack-error-700': idx === 0,
					})}>
					{i.label} {i.unit}
				</span>
			))}
		</div>
	)
}

const MainType: React.FC<{
	text: string
	type: 'Parent Product' | 'กล่องฝาชน' | 'Child Product' | 'ที่ต้องชำระ'
	isSub?: boolean
}> = ({ text, type, isSub }) => {
	const buttonClass = () => {
		switch (type) {
			case 'Parent Product':
				return 'btn-kumopack-secondary-50 text-kumopack-secondary-600'
			case 'กล่องฝาชน':
				return 'btn-kumopack-grey-200 text-kumopack-grey-700'
			case 'Child Product':
				return 'btn-kumopack-grey-200 text-kumopack-grey-700'
			case 'ที่ต้องชำระ':
				return 'btn-kumopack-success-700-inverse'
		}
	}

	if (isSub) {
		return null
	}

	return (
		<div
			className={`btn btn-sm text-grey ${buttonClass()} px-4 py-1`}
			style={{
				borderRadius: '16px',
			}}>
			{text}
		</div>
	)
}

// eslint-disable-next-line
const TotalPurchase = ({ value }: { value: string }) => {
	const isNegativeOrZero = () => {
		return parseFloat(value) <= 0
	}

	return (
		<div className="d-flex flex-column" style={{ columnGap: '8px' }}>
			<div className="d-flex flex-column">
				<span className="fw-bold">
					{formatNumberCommas(isNegativeOrZero() ? parseFloat(value) * -1 : parseFloat(value))}
				</span>
			</div>

			{parseFloat(value) > 0 ? (
				<div
					className="bg-kumopack-success-50 text-kumopack-success-700 px-2 w-fit-content"
					style={{ borderRadius: '16px' }}>
					<img src="/media/icons/kumopack/arrow-up-green.png" alt="Percentage Changing Icon" /> 20%
				</div>
			) : (
				<div
					className="bg-kumopack-error-50 text-kumopack-error-700 px-2 w-fit-content"
					style={{ borderRadius: '16px' }}>
					<img src="/media/icons/kumopack/arrow-down-red.png" alt="Percentage Changing Icon" /> 20%
				</div>
			)}
		</div>
	)
}

interface Props {
	products: any[]
}

const ProductTable: React.FC<Props> = ({ products }) => {
	const { updateLoading } = useContext(TableContext)

	const setupTable = () => {
		updateLoading(false)
	}

	const TABLE_CONFIGS: any[] = [
		// {
		// 	Header: <Checkbox value={false} />,
		// 	accessor: 'checkbox',
		// 	width: 50,
		// 	minWidth: 50,
		// 	Cell: ({ value }: any) => <Checkbox value={value} />,
		// },
		{
			Header: 'ชื่อสินค้า/รหัสสินค้า',
			width: 200,
			minWidth: 200,
			accessor: 'product_type',
			Cell: ({ value, row }: any) => {
				return (
					<ProductType
						value={value}
						product_num={row.original.product_num}
						product_img={row.original.product_img}
						isSub={row.original?.isSub}
					/>
				)
			},
		},
		{
			Header: 'การมองเห็น',
			width: 200,
			mintWidth: 200,
			accessor: 'visibility',
			Cell: ({ value, row }: any) => <ProductVisiblity {...value} isSub={row.original?.isSub} />,
		},
		{
			Header: 'ประเภทสินค้าหลัก',
			accessor: 'type',
			width: 150,
			minWidth: 150,
			Cell: ({ value, row }: any) => (
				<MainType isSub={row.original.isSub} text={value} type={value} />
			),
		},
		// {
		// 	Header: 'ตัวเลือกสินค้า',
		// 	accessor: 'product_options',
		// 	width: 150,
		// 	minWidth: 150,
		// 	Cell: ({ value }: any) => <ProductChip items={value} />,
		// },
		{
			Header: 'จำนวนสินค้า',
			accessor: 'stockQuantity',
			width: 150,
			minWidth: 150,
			Cell: ({ value }: any) => {
				return <div className="fw-bold">{formatNumberCommasNoDecimal(value)}</div>
			},
		},
		{
			Header: 'ราคา',
			accessor: 'price',
			width: 150,
			minWidth: 150,
			Cell: ({ value }: any) => <div className="fw-bold">{formatNumberCommas(value)}฿</div>,
		},
		// {
		// 	Header: 'ยอดซื้อทั้งหมด',
		// 	accessor: 'total_purchase',
		// 	width: 130,
		// 	minWidth: 130,
		// 	Cell: ({ value }: any) => <TotalPurchase value={value} />,
		// },
		{
			Header: 'ใช้งานอยู่',
			accessor: 'isActive',
			width: 100,
			minWidth: 100,
			Cell: ({ value, row }: any) => <ProductStatus value={value} />,
		},
		{
			Header: '',
			accessor: 'action',
			width: 20,
			minWidth: 20,
			Cell: () => (
				<div className="w-100 text-center">
					<button className="btn btn-icon btn-custom btn-active-light rounded-circle">
						<img
							src="/media/icons/kumopack/dots-vertical.png"
							alt="Dot Vertical Icon"
							width={20}
							height={20}
						/>
					</button>
				</div>
			),
			disableSortBy: true,
		},
	]

	useEffect(() => {
		setupTable()
		// eslint-disable-next-line
	}, [])

	return (
		<>
			{/* begin:: Body */}
			<div className="card-body px-0 pt-0">
				<ClientSideTable
					pagination
					isGrey
					items_per_page={10}
					columns={TABLE_CONFIGS}
					data={products}
				/>
			</div>
			{/* end:: Body */}
		</>
	)
}

export default ProductTable
